import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

/**
 * Origin type for synonyms
 */
export type SynonymOriginType = 'manual' | 'ai_product' | 'ai_category' | 'ai_general'

/**
 * Synonym group type
 */
export interface SynonymGroup {
  id: string
  term: string
  synonyms: string[]
  enabled: boolean
  // AI fields
  originType: SynonymOriginType
  originId: string | null
  originName: string | null
  groupName: string | null
  confidence: number | null
  generatedAt: number | null
  createdAt: number
  updatedAt: number
}

/**
 * Generation log type
 */
export interface GenerationLog {
  id: string
  startedAt: number
  completedAt: number | null
  productsAnalyzed: number
  categoriesAnalyzed: number
  synonymsGenerated: number
  synonymsMerged: number
  modelUsed: string | null
  status: 'running' | 'complete' | 'error'
  error: string | null
}

/**
 * Fetch all synonyms
 */
export function useSynonyms() {
  return useQuery({
    queryKey: ['synonyms'],
    queryFn: async (): Promise<SynonymGroup[]> => {
      const res = await fetch('/api/synonyms', { credentials: 'include' })
      if (!res.ok) {
        throw new Error('Failed to fetch synonyms')
      }
      const data = (await res.json()) as { synonyms: SynonymGroup[] }
      return data.synonyms || []
    },
  })
}

/**
 * Create a new synonym group
 */
export function useCreateSynonym() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { term: string; synonyms: string[] }): Promise<SynonymGroup> => {
      const res = await fetch('/api/synonyms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const errorData = (await res.json()) as { error?: string }
        throw new Error(errorData.error || 'Failed to create synonym')
      }
      return res.json() as Promise<SynonymGroup>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms'] })
    },
  })
}

/**
 * Update a synonym group
 */
export function useUpdateSynonym() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { term?: string; synonyms?: string[]; enabled?: boolean }
    }): Promise<SynonymGroup> => {
      const res = await fetch(`/api/synonyms/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const errorData = (await res.json()) as { error?: string }
        throw new Error(errorData.error || 'Failed to update synonym')
      }
      return res.json() as Promise<SynonymGroup>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms'] })
    },
  })
}

/**
 * Delete a synonym group
 */
export function useDeleteSynonym() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`/api/synonyms/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) {
        const errorData = (await res.json()) as { error?: string }
        throw new Error(errorData.error || 'Failed to delete synonym')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms'] })
    },
  })
}

// ============================================================
// AI Generation Hooks
// ============================================================

/**
 * Fetch generation logs
 */
export function useGenerationLogs() {
  return useQuery({
    queryKey: ['synonym-generation-logs'],
    queryFn: async (): Promise<GenerationLog[]> => {
      const res = await fetch('/api/synonyms/generation-log', { credentials: 'include' })
      if (!res.ok) {
        throw new Error('Failed to fetch generation logs')
      }
      const data = (await res.json()) as { logs: GenerationLog[] }
      return data.logs || []
    },
  })
}

/**
 * Get latest generation log (for status display)
 */
export function useLatestGenerationLog() {
  return useQuery({
    queryKey: ['synonym-generation-latest'],
    queryFn: async (): Promise<GenerationLog | null> => {
      const res = await fetch('/api/synonyms/generation-log/latest', { credentials: 'include' })
      if (!res.ok) {
        throw new Error('Failed to fetch latest generation log')
      }
      const data = (await res.json()) as { log: GenerationLog | null }
      return data.log
    },
  })
}

/**
 * Trigger AI synonym generation
 */
export function useGenerateSynonyms() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<{ status: string; logId: string }> => {
      const res = await fetch('/api/synonyms/generate', {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) {
        const errorData = (await res.json()) as { error?: string }
        throw new Error(errorData.error || 'Failed to start generation')
      }
      return res.json() as Promise<{ status: string; logId: string }>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonym-generation-logs'] })
      queryClient.invalidateQueries({ queryKey: ['synonym-generation-latest'] })
    },
  })
}

/**
 * Poll generation status
 */
export function useGenerationStatus(logId: string | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['synonym-generation-status', logId],
    queryFn: async (): Promise<GenerationLog | null> => {
      if (!logId) return null
      const res = await fetch(`/api/synonyms/generate/status?logId=${logId}`, {
        credentials: 'include',
      })
      if (!res.ok) {
        throw new Error('Failed to fetch generation status')
      }
      const data = (await res.json()) as { log: GenerationLog | null }
      return data.log
    },
    enabled: enabled && !!logId,
    refetchInterval: (query) => {
      const data = query.state.data
      // Poll every 2 seconds while running, stop when complete/error
      if (data?.status === 'running') return 2000
      return false
    },
  })
}

/**
 * Delete all AI-generated synonyms
 */
export function useDeleteAISynonyms() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => {
      const res = await fetch('/api/synonyms/ai-generated', {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) {
        const errorData = (await res.json()) as { error?: string }
        throw new Error(errorData.error || 'Failed to delete AI synonyms')
      }
      return res.json() as Promise<{ deleted: number }>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms'] })
      queryClient.invalidateQueries({ queryKey: ['synonym-generation-logs'] })
      queryClient.invalidateQueries({ queryKey: ['synonym-generation-latest'] })
    },
  })
}

/**
 * Get unique group names for filtering
 */
export function useGroupNames() {
  return useQuery({
    queryKey: ['synonym-group-names'],
    queryFn: async (): Promise<string[]> => {
      const res = await fetch('/api/synonyms/groups', { credentials: 'include' })
      if (!res.ok) {
        throw new Error('Failed to fetch group names')
      }
      const data = (await res.json()) as { groups: string[] }
      return data.groups || []
    },
  })
}
